import { z } from 'zod';

import {
  DemandPartnerCpmMetric,
  DemandPartnerDynamicField,
  DemandPartnerGrossNet,
  DemandPartnerPricingType,
  DemandPartnerStatus,
} from './model';

const requiredText = z.string().trim().min(1, 'This field is required.');
const optionalNumber = z.union([z.number(), z.nan()]).optional();
const optionalNonWhitespace = z.union([
  z.literal(''),
  z.string().trim().min(1, 'TCF Vendor ID cannot contain only whitespace.'),
]);

export const demandPartnerFormSchema = z
  .object({
    id: z.string(),
    displayName: requiredText,
    ownerId: z.string(),
    tcfVendorId: optionalNonWhitespace,
    bidderCode: requiredText,
    address: z.string(),
    status: z.enum(DemandPartnerStatus),
    approvalsRequired: z.boolean(),
    urlCleanup: z.boolean(),
    skipRequestsWithoutContentUrl: z.boolean(),
    nullifyContentChannelNetwork: z.boolean(),
    contacts: z.array(
      z.object({
        id: z.string().optional(),
        name: z.string(),
        role: z.string(),
        email: z.string(),
        phone: z.string(),
      }),
    ),
    pricingType: z.enum(DemandPartnerPricingType).nullable(),
    revShareValue: optionalNumber,
    revShareGrossNet: z.enum(DemandPartnerGrossNet),
    ioCpmCostValue: optionalNumber,
    ioCpmCostMetric: z.enum(DemandPartnerCpmMetric),
    additionalCost: optionalNumber,
    useStickyPricing: z.boolean(),
    biddingGrossNet: z.enum(DemandPartnerGrossNet),
    biddingTmax: optionalNumber,
    biddingBidTTL: optionalNumber,
    revenueCapEnabled: z.boolean(),
    revenueCap: optionalNumber,
    includeUserIdInCacheKey: z.boolean(),
    userSyncRedirectEnabled: z.boolean(),
    userSyncRedirect: z.string(),
    userSyncIframeEnabled: z.boolean(),
    userSyncIframe: z.string(),
    qps: z.number().nullable(),
    cacheParamsVisible: z.boolean(),
    cacheParamsEnabled: z.boolean(),
    cacheUa: z.boolean(),
    cacheIp: z.boolean(),
    cacheBundle: z.boolean(),
    cacheCountry: z.boolean(),
    cacheCity: z.boolean(),
    includeBundles: z.boolean(),
    includeDomains: z.boolean(),
    requiredDynamicFields: z.array(z.enum(DemandPartnerDynamicField)),
  })
  .superRefine((value, context) => {
    if (value.qps !== null && (value.qps < 0 || value.qps > 5787)) {
      context.addIssue({
        code: 'custom',
        path: ['qps'],
        message: 'QPS must be between 0 and 5787.',
      });
    }
    const nonnegativeFields = [
      'revShareValue',
      'ioCpmCostValue',
      'additionalCost',
      'biddingTmax',
      'biddingBidTTL',
    ] as const;
    nonnegativeFields.forEach((field) => {
      const number = value[field];
      if (number !== undefined && !Number.isNaN(number) && number < 0) {
        context.addIssue({ code: 'custom', path: [field], message: 'Must be non-negative.' });
      }
    });
    if (
      value.revenueCapEnabled &&
      (value.revenueCap === undefined || Number.isNaN(value.revenueCap) || value.revenueCap < 0)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['revenueCap'],
        message: 'Daily revenue cap is required and must be non-negative.',
      });
    }
    if (value.userSyncRedirectEnabled && !value.userSyncRedirect.trim()) {
      context.addIssue({
        code: 'custom',
        path: ['userSyncRedirect'],
        message: 'Redirect URL is required.',
      });
    }
    if (value.userSyncIframeEnabled && !value.userSyncIframe.trim()) {
      context.addIssue({
        code: 'custom',
        path: ['userSyncIframe'],
        message: 'Iframe URL is required.',
      });
    }
    value.requiredDynamicFields.forEach((field) => {
      if (
        field === DemandPartnerDynamicField.AdditionalCost &&
        (value.additionalCost === undefined || Number.isNaN(value.additionalCost))
      ) {
        context.addIssue({
          code: 'custom',
          path: ['additionalCost'],
          message: 'Additional cost is required.',
        });
      }
    });
  });

export type DemandPartnerFormValue = z.infer<typeof demandPartnerFormSchema>;

export const emptyDemandPartnerFormValue: DemandPartnerFormValue = {
  id: '',
  displayName: '',
  ownerId: '',
  tcfVendorId: '',
  bidderCode: '',
  address: '',
  status: DemandPartnerStatus.Active,
  approvalsRequired: false,
  urlCleanup: false,
  skipRequestsWithoutContentUrl: false,
  nullifyContentChannelNetwork: false,
  contacts: [],
  pricingType: null,
  revShareValue: undefined,
  revShareGrossNet: DemandPartnerGrossNet.Net,
  ioCpmCostValue: undefined,
  ioCpmCostMetric: DemandPartnerCpmMetric.AdRequest,
  additionalCost: undefined,
  useStickyPricing: false,
  biddingGrossNet: DemandPartnerGrossNet.Gross,
  biddingTmax: undefined,
  biddingBidTTL: undefined,
  revenueCapEnabled: false,
  revenueCap: undefined,
  includeUserIdInCacheKey: false,
  userSyncRedirectEnabled: false,
  userSyncRedirect: '',
  userSyncIframeEnabled: false,
  userSyncIframe: '',
  qps: null,
  cacheParamsVisible: false,
  cacheParamsEnabled: false,
  cacheUa: false,
  cacheIp: false,
  cacheBundle: false,
  cacheCountry: false,
  cacheCity: false,
  includeBundles: true,
  includeDomains: true,
  requiredDynamicFields: [],
};
